import {
  ArrowLeft,
  Target,
  Camera,
  Link as LinkIcon,
  BarChart3,
  Bell,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const GettingStartedPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: Target,
      title: "Set Your Goals",
      description:
        "Start by setting your health goals in your profile. Whether you want to lose weight, build muscle, or maintain your current health, Bite Balance adapts to your needs.",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      number: 2,
      icon: Camera,
      title: "Log Your Meals",
      description:
        "Use the camera feature to quickly scan and log your meals. Our AI will analyze the content and track your daily intake automatically.",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      number: 3,
      icon: LinkIcon,
      title: "Connect Your Device",
      description:
        "Link your Fitbit or other fitness devices to automatically sync your activity, sleep, and health metrics for a complete picture of your wellness.",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      number: 4,
      icon: BarChart3,
      title: "Track Daily Nutrition",
      description:
        "Monitor your macros, calories, and nutrition scores. Get personalized insights on how your diet affects your overall health and energy levels.",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      number: 5,
      icon: Bell,
      title: "Enable Notifications",
      description:
        "Set up meal reminders and health alerts to stay on track with your goals. Customize when and how you receive updates.",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-6 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Getting Started</h1>
        </div>

        {/* Welcome Section */}
        <div className="space-y-2">
          <h2 className="text-title3 font-bold text-foreground">
            Welcome to Bite Balance!
          </h2>
          <p className="text-subhead text-muted-foreground">
            Follow these simple steps to get the most out of your health
            tracking journey.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {steps.map(step => {
            const Icon = step.icon;
            return (
              <Card
                key={step.number}
                className="flex gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm"
              >
                {/* Icon with notification badge */}
                <div className="relative flex-shrink-0">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${step.bgColor}`}
                  >
                    <Icon className={`h-6 w-6 ${step.color}`} />
                  </div>
                  <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black text-caption1 font-bold text-white">
                    {step.number}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-1 pt-1">
                  <h3 className="font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-subhead text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Footer Help Text */}
        <div className="pt-4 text-center text-subhead text-muted-foreground">
          Need more help? Check out our FAQs or contact support.
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;
